<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";

defineOptions({
  name: "VTableFooter",
});

/**
 * The bottom bar of a VTable. Lists load more while scrolling: it shows "Geladen: 10 van 24 gebruikers" and, while
 * it loads, "Meer gebruikers laden".
 */
const {
  loadedCount,
  totalItems = null,
  fetchingMore = false,
  itemsLabel,
} = defineProps<{
  loadedCount: number
  totalItems?: number | null
  fetchingMore?: boolean
  itemsLabel: string
}>();

const { t, n } = useI18n();

const countText = computed(() => {
  if (totalItems !== null && totalItems > loadedCount) {
    return t("base.table.footer.loaded", { count: n(loadedCount), total: n(totalItems), items: itemsLabel });
  }
  return t("base.table.footer.count", { count: n(totalItems ?? loadedCount), items: itemsLabel });
});
</script>

<template>
  <div
    class="v-table-footer flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[13px] text-gray-700 dark:border-dark-600 dark:bg-dark-700 dark:text-gray-400"
    data-testid="table-footer"
  >
    <span role="status" class="tabular-nums">{{ countText }}</span>
    <span
      v-if="fetchingMore"
      class="inline-flex items-center gap-2 font-medium text-primary-900 dark:text-primary-400"
      role="status"
      data-testid="table-footer-loading-more"
    >
      <VIcon name="loader-circle" class="animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {{ t("base.table.footer.loading_more", { items: itemsLabel }) }}
    </span>
  </div>
</template>
